import { useState } from 'react'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useFacilityMutations } from '@/hooks/useFacility'
import { useTenant } from '@/providers/TenantProvider'
import { facilityExample } from '@/lib/facilityStudio'
import type { Facility, FacilityInput } from '@/api/facilityApi'

/** The facility example, made from the selected tenant's name rather than a stranger's site. */
export function useFacilityExample() {
  const { selectedTenant } = useTenant()
  return facilityExample(selectedTenant?.name)
}

/**
 * The facility a set-up is working in (z8nrz7fw25), e.g. "NYP". A facility is
 * configured once here and then picked: its buildings, floors and rooms hang
 * under it, so nobody types the site again on a unit.
 *
 * Deleting one is offered only when it has no buildings; the server refuses it
 * otherwise, so the button never promises what cannot happen.
 */
export function FacilityBar({
  facilities,
  selectedId,
  onSelect,
  buildingCount,
  run,
}: {
  facilities: Facility[]
  selectedId: string | null
  onSelect: (id: string) => void
  /** Buildings in the selected facility. */
  buildingCount: number
  run: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const mut = useFacilityMutations()
  const example = useFacilityExample()
  const [mode, setMode] = useState<'view' | 'add' | 'edit'>('view')
  const selected = facilities.find((f) => f.id === selectedId) ?? null

  if (mode !== 'view') {
    const editing = mode === 'edit' ? selected : null
    return (
      <FacilityForm
        initial={editing ?? undefined}
        submitLabel={editing ? 'Save facility' : 'Add facility'}
        onCancel={() => setMode('view')}
        onSubmit={(v) =>
          run(async () => {
            if (editing) {
              await mut.updateFacility.mutateAsync({ id: editing.id, ...v })
            } else {
              const created = await mut.createFacility.mutateAsync(v)
              onSelect(created.id)
            }
            setMode('view')
          })
        }
      />
    )
  }

  return (
    <div className='space-y-1.5 rounded-lg border bg-white p-2.5'>
      <div className='flex items-center justify-between'>
        <h3 className='flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
          <Building2 className='h-3.5 w-3.5' /> Facility
        </h3>
        <div className='flex'>
          {selected && (
            <Button type='button' variant='ghost' size='sm' className='h-6 w-6 p-0' aria-label='Edit facility' onClick={() => setMode('edit')}>
              <Pencil className='h-3.5 w-3.5' />
            </Button>
          )}
          {selected && buildingCount === 0 && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-6 w-6 p-0 text-destructive hover:text-destructive'
              aria-label='Delete facility'
              onClick={() => {
                if (window.confirm(`Delete ${selected.name}? It has no buildings.`)) {
                  void run(() => mut.deleteFacility.mutateAsync(selected.id))
                }
              }}
            >
              <Trash2 className='h-3.5 w-3.5' />
            </Button>
          )}
          <Button type='button' variant='ghost' size='sm' className='h-6 w-6 p-0' aria-label='Add facility' onClick={() => setMode('add')}>
            <Plus className='h-3.5 w-3.5' />
          </Button>
        </div>
      </div>
      {facilities.length === 0 ? (
        <p className='text-[11px] text-muted-foreground'>
          None yet. Add one, e.g. {example.code}; the first building you add makes one for you otherwise.
        </p>
      ) : (
        <Select value={selected?.id ?? ''} onValueChange={onSelect}>
          <SelectTrigger className='h-8' aria-label='Facility'>
            <SelectValue placeholder='Choose a facility…' />
          </SelectTrigger>
          <SelectContent>
            {facilities.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                <span className='font-semibold'>{f.code}</span> · {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {selected?.address && <p className='truncate text-[11px] text-muted-foreground'>{selected.address}</p>}
    </div>
  )
}

export function FacilityForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Facility
  submitLabel: string
  onSubmit: (v: FacilityInput) => Promise<void>
  /** Omitted where there is nothing to go back to, such as a site's first facility. */
  onCancel?: () => void
}) {
  const [v, setV] = useState({
    code: initial?.code ?? '',
    name: initial?.name ?? '',
    address: initial?.address ?? '',
  })
  const [busy, setBusy] = useState(false)
  const example = useFacilityExample()
  const valid = v.code.trim() !== '' && v.name.trim() !== ''
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement>) => setV((p) => ({ ...p, [k]: e.target.value }))
  return (
    <form
      className='space-y-2 rounded-lg border bg-white p-2.5'
      onSubmit={(e) => {
        e.preventDefault()
        if (!valid) return
        setBusy(true)
        void onSubmit({ code: v.code.trim(), name: v.name.trim(), address: v.address.trim() || null }).finally(() =>
          setBusy(false),
        )
      }}
    >
      <label className='block text-[11px] font-medium text-gray-600'>
        Code
        <Input value={v.code} onChange={set('code')} maxLength={16} placeholder={`e.g. ${example.code}`} className='mt-1 h-8' aria-label='Facility code' />
      </label>
      <label className='block text-[11px] font-medium text-gray-600'>
        Name
        <Input value={v.name} onChange={set('name')} placeholder={`e.g. ${example.name}`} className='mt-1 h-8' aria-label='Facility name' />
      </label>
      <label className='block text-[11px] font-medium text-gray-600'>
        Address
        <Input value={v.address} onChange={set('address')} className='mt-1 h-8' aria-label='Facility address' />
      </label>
      <div className='flex justify-end gap-2'>
        {onCancel && (
          <Button type='button' variant='ghost' size='sm' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type='submit' size='sm' disabled={!valid || busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
